import { Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import {
  HostControlDirective,
  InputError,
  PasswordErrorRequired,
  PasswordPolicies,
  PasswordValidationService,
} from '@ebeihilfe/core';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { ProgressBarComponent } from '../progress-bar';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'portal-password-input',
  imports: [
    MatFormField,
    TranslateModule,
    ReactiveFormsModule,
    MatInput,
    MatIconButton,
    MatIcon,
    MatFormFieldModule,
    ProgressBarComponent,
  ],
  templateUrl: './password-input.component.html',
  hostDirectives: [HostControlDirective],
})
export class PasswordInputComponent implements OnInit {
  private readonly passwordValidationService = inject(PasswordValidationService);
  public _errors = input<InputError[]>([], { alias: 'errors' });
  public accessorDirective = inject(HostControlDirective);
  public autocomplete = input<string>('current-password');
  public createPasswordMode = input<boolean>(false);
  public errors = signal([] as InputError[]);
  public hideRequiredMarker = input<boolean>(true);
  public hint = input<string>(_('info_text.input.password'));
  public id = input<string>('inputPassword');
  public inputmode = input<'text' | 'numeric'>('text');
  public label = input<string>(_('label.password'));
  public placeholder = input<string>();
  public showMatError = signal(false);
  public showPassword = signal(false);
  public passwordRevealIcon = computed(() => (this.showPassword() ? 'visibility_off' : 'visibility'));
  public fieldType = computed(() => (this.showPassword() ? 'text' : 'password'));
  public showPlaceholder = computed(() => {
    const placeholder = this.placeholder();
    const labelFromInput = this.label();
    return !(placeholder === undefined || placeholder === labelFromInput);
  });
  public passwordRules = signal<PasswordPolicies | null>(null);

  private destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.passwordValidationService.policies$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((policies) => {
      if (policies) {
        this.passwordRules.set(policies);
        this.updateValidatorsAndErrors(policies);
      }
    });
    this.handleChangeControl();
  }

  public togglePasswordVisibility(): void {
    this.showPassword.update((value) => !value);
  }

  private updateValidatorsAndErrors(policies: PasswordPolicies): void {
    const control = this.accessorDirective.control;
    this.errors.set(this._errors());

    if (control) {
      const validators = [Validators.required];
      this.addToErrors(PasswordErrorRequired);

      const rules = policies.passwordPolicies ?? [];
      const minLength = policies.minimumLength;
      const maxLength = policies.maximumLength;

      if (this.createPasswordMode() && (rules.length > 0 || minLength > 0)) {
        validators.push(this.passwordValidationService.createPasswordValidator(rules, minLength, maxLength));
      }

      control.addValidators(validators);
      control.updateValueAndValidity();
    }
  }

  private handleChangeControl(): void {
    this.accessorDirective.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.showMatError.set(
          this.accessorDirective.control.invalid &&
            (this.accessorDirective.control.dirty || this.accessorDirective.control.touched),
        ),
      );
  }

  private addToErrors(error: InputError): void {
    this.errors.set([...this.errors(), error]);
  }
}
